import React, { memo } from 'react';
import PropTypes from 'prop-types';

import moment from 'moment';

import {
  ButtonBody,
  Cell,
  Closed,
  Day,
  LowAvailability,
  Month,
  CustomText,
  IconWrapper,
  CalendarIcon,
} from './CalendarDayCell.style';
import IbePropTypes from '../../../../../IbePropTypes';
import CAMPSITE_STATUS, { CAMPSITE_CALENDAR_TEXT } from '../../../../../constants/campsiteStatus';

import { dateIsInRange } from '../../../../../lib/dates';

import svgBookedTick from '../../../../../static/images/icons/BookedTick.svg';
import svgError from '../../../../../static/images/icons/Error.svg';

import DayCell from './CalendarDayCell.variants';
import { SELECTED_DATES_STAY_STATUS } from '../../../../Availability/constants';

const propTypes = {
  availabilitySearchLoading: PropTypes.bool,
  availabilityTotalLoading: PropTypes.bool,
  bookingEndDate: PropTypes.string,
  bookingStartDate: PropTypes.string,
  date: PropTypes.shape(IbePropTypes.moment).isRequired,
  disabled: PropTypes.bool.isRequired,
  endDate: PropTypes.string,
  handleClick: PropTypes.func.isRequired,
  hasLowAvailability: PropTypes.bool,
  isBookedPitch: PropTypes.bool,
  isLastDayInOpenRange: PropTypes.bool,
  isNeutralCalendar: PropTypes.bool,
  isSelectedEventClosed: PropTypes.bool,
  isSelectedEventClosedLabel: PropTypes.string,
  showMemberBookableOnly: PropTypes.bool,
  userMustBookAcrossPitches: PropTypes.number,
  loading: PropTypes.bool,
  lowAvailabilityLabel: PropTypes.string,
  month: PropTypes.string,
  placeholder: PropTypes.string,
  showMonth: PropTypes.bool,
  showRemainder: PropTypes.bool.isRequired,
  startDate: PropTypes.string.isRequired,
  text: PropTypes.string,
  visible: PropTypes.bool.isRequired,
  onRequest: PropTypes.bool,
  variant: PropTypes.string,
  isSearchFormPicker: PropTypes.bool,
  'data-testid': PropTypes.string,
};

const defaultProps = {
  availabilitySearchLoading: false,
  availabilityTotalLoading: false,
  bookingEndDate: '',
  bookingStartDate: '',
  endDate: '',
  hasLowAvailability: false,
  isBookedPitch: false,
  isLastDayInOpenRange: false,
  isNeutralCalendar: false,
  isSelectedEventClosed: false,
  isSelectedEventClosedLabel: CAMPSITE_STATUS.CLOSED,
  userMustBookAcrossPitches: SELECTED_DATES_STAY_STATUS.CONTINUOUS_PITCH_AVAILABLE,
  showMemberBookableOnly: false,
  loading: false,
  lowAvailabilityLabel: 'LOW',
  month: '',
  placeholder: '',
  showMonth: false,
  text: '',
  onRequest: false,
  variant: '',
  isSearchFormPicker: false,
  'data-testid': null,
};

function CalendarDayCell(props) {
  const {
    availabilitySearchLoading,
    availabilityTotalLoading,
    bookingStartDate,
    bookingEndDate,
    date,
    disabled,
    endDate,
    handleClick,
    hasLowAvailability,
    isBookedPitch,
    isLastDayInOpenRange,
    isNeutralCalendar,
    isSelectedEventClosed,
    isSelectedEventClosedLabel,
    userMustBookAcrossPitches,
    showMemberBookableOnly,
    loading,
    lowAvailabilityLabel,
    month,
    placeholder,
    showMonth,
    showRemainder,
    startDate,
    text,
    variant,
    visible,
    onRequest,
  } = props;

  const selected = date.isSame(endDate) || date.isSame(startDate);

  const isEndDate = date.isSame(endDate) && !date.isSame(startDate);

  const rangedMatch = date.isSame(startDate) || date.isSame(endDate) ||
    (date.isAfter(startDate) && date.isBefore(endDate));

  const inRange = endDate
    ? rangedMatch
    : null;

  const isBookedDay = isBookedPitch && dateIsInRange(date, [bookingStartDate, bookingEndDate]);

  const isLastBookedDay = isBookedPitch && date.isSame(bookingEndDate);

  const isTickableForAmend = isBookedDay && !isLastBookedDay;

  const isLoading = availabilityTotalLoading || availabilitySearchLoading;

  const isSelectedEventFull = !isLoading &&
    (text === CAMPSITE_CALENDAR_TEXT.SITE_FULL || text === CAMPSITE_CALENDAR_TEXT.PITCH_FULL);

  const isAmend = !!bookingStartDate;

  // Don't show the 'LOW' indicator in selected cells on amends
  const isLowAndBooked = hasLowAvailability && isBookedDay;
  const isAmendingAndLowAndActive = isAmend && isLowAndBooked;

  const shouldShowLowAvailabilityIndicator =
    !isSelectedEventClosed
    && hasLowAvailability
    && !disabled
    && !isAmendingAndLowAndActive
    && !onRequest
    && !isLoading;

  // This tick shows on the neutral calendar when users must
  // book across pitches to have their desired dates
  const mustBookAcrossPitches =
    userMustBookAcrossPitches === SELECTED_DATES_STAY_STATUS.MUST_BOOK_ACROSS_PITCHES;
  const showMustBookAcrossPitchesTick =
    !isAmend
    && isNeutralCalendar
    && mustBookAcrossPitches
    && inRange
    && !isEndDate;

  return (
    <Cell
      disabled={disabled}
      visible={visible}
      data-variant={variant}
    >
      {(showRemainder || visible) &&
        <DayCell
          variant={props.variant}
          aria-label="Calendar day"
          type="button"
          disabled={disabled || loading}
          notAllowed={showMemberBookableOnly}
          onClick={() => handleClick(date, isLastDayInOpenRange)}
          isSearchFormPicker={props.isSearchFormPicker}
          data-testid={
            props['data-testid'] ? props['data-testid'] : null
          }
        >
          <ButtonBody>
            {shouldShowLowAvailabilityIndicator &&
              <LowAvailability highlight={rangedMatch || showMemberBookableOnly}>
                {lowAvailabilityLabel}
              </LowAvailability>
            }
            {(!isLoading && isSelectedEventClosed) &&
              <Closed highlight={inRange} isSelectedEventClosed={isSelectedEventClosed}>
                {isSelectedEventClosedLabel}
              </Closed>
            }
            <Day withMonth={showMonth && hasLowAvailability} hasText={placeholder !== null || text}>
              {moment(date).format('D')}
              {showMonth &&
                <Month withMonth={showMonth && hasLowAvailability}>{month}</Month>
              }
            </Day>
            <CustomText
              contain={showMemberBookableOnly || onRequest}
              isLastSelectedDay={isEndDate}
              isSelectedEventClosed={isSelectedEventClosed}
              isSelectedEventFull={isSelectedEventFull}
              isSelectedEventContact={onRequest}
            >
              <div>{text}</div>
            </CustomText>
            {(!text && !disabled && placeholder) &&
              <CustomText
                isLastSelectedDay={isEndDate}
                isSelectedEventClosed={isSelectedEventClosed}
              >
                <div>{placeholder}</div>
              </CustomText>
            }
            {isTickableForAmend && (
              <IconWrapper>
                <CalendarIcon
                  icon={svgBookedTick}
                  isSelected={selected || inRange}
                  isBookedDay={isBookedDay}
                  isLastSelectedDay={isEndDate}
                  size={`
                    width: 21px;
                    height: 18px;
                  `}
                />
              </IconWrapper>
            )}
            {showMustBookAcrossPitchesTick && (
              <IconWrapper>
                <CalendarIcon
                  icon={svgError}
                  isSelected={selected || inRange}
                  isBookedDay={isBookedDay}
                  isLastSelectedDay={isEndDate}
                  size={`
                    width: 24px;
                    height: 24px;
                  `}
                />
              </IconWrapper>
            )}
          </ButtonBody>
        </DayCell>
      }
    </Cell>
  );
}

CalendarDayCell.propTypes = propTypes;
CalendarDayCell.defaultProps = defaultProps;

export default memo(CalendarDayCell);
